import type { GenericAvailabilityType, Maybe } from '../cards'
import {
  AvailabilityStatus,
  AvailabilityStatusPresentation,
  AvailabilityStatusType,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { Translator } from '@eo-locale/core'
import { AvailabilityText } from './product-availability-test'

export type IconColor = 'white' | 'green' | 'orange' | 'gray'
export const AvailabilityTypes = {
  SHOW_ALWAYS_IN_STOCK: 'SHOW_ALWAYS_IN_STOCK',
  IN_STOCK_IMMEDIATE_SHIPPING: 'IN_STOCK_IMMEDIATE_SHIPPING',
  SUPPLIER_STOCK_SHIPPING_WITHIN_48H: 'SUPPLIER_STOCK_SHIPPING_WITHIN_48H',
  SUPPLIER_STOCK_SHIPPING_WITHIN_DAYS: 'SUPPLIER_STOCK_SHIPPING_WITHIN_DAYS',
  NON_STOCK_ITEM: 'NON_STOCK_ITEM',
  ESTIMATED_IN_STORE_BY_DATE: 'ESTIMATED_IN_STORE_BY_DATE',
  IMMEDIATE_DOWNLOAD: 'IMMEDIATE_DOWNLOAD',
  NON_STOCK_ITEM_EARLIEST_DATE: 'NON_STOCK_ITEM_EARLIEST_DATE',
  WEEKS_FROM_ORDER_DATE: 'WEEKS_FROM_ORDER_DATE',
  NOT_MIGRATED_TYPE: 'NOT_MIGRATED_TYPE',
  PRODUCT_AVAILABILITY_OUT_OF_STOCK_COMING_SOON: 'PRODUCT_AVAILABILITY_OUT_OF_STOCK_COMING_SOON',
} as const

type AvailabilityTypeKeys = keyof typeof AvailabilityTypes
type AvailabilityType = (typeof AvailabilityTypes)[AvailabilityTypeKeys]

const LONGER_SHIPPING_TYPES = [
  AvailabilityStatusType.ProductDistributorLeadTimeThreeFourDays,
  AvailabilityStatusType.ProductDistributorLeadTimeFourSixDays,
  AvailabilityStatusType.ProductDistributorLeadTimeSixEightDays,
  AvailabilityStatusType.ProductDistributorLeadTimeTenTwelveDays,
]

type StockQuantities = {
  internal: number
  external: number
  internalEta: number
}

const SHIPPING_DAYS_TRANSLATIONS = {
  PRODUCT_DISTRIBUTOR_LEAD_TIME_THREE_FOUR_DAYS:
    'AvailabilityText_ProductDistributorLeadTimeThreeFourDays',
  PRODUCT_DISTRIBUTOR_LEAD_TIME_FOUR_SIX_DAYS:
    'AvailabilityText_ProductDistributorLeadTimeFourSixDays',
  PRODUCT_DISTRIBUTOR_LEAD_TIME_SIX_EIGHT_DAYS:
    'AvailabilityText_ProductDistributorLeadTimeSixEightDays',
  PRODUCT_DISTRIBUTOR_LEAD_TIME_TEN_TWELVE_DAYS:
    'AvailabilityText_ProductDistributorLeadTimeTenTwelveDays',
} as const

export function getAvailabilityType(
  availability?: Maybe<GenericAvailabilityType>
): AvailabilityType {
  if (!availability) {
    return AvailabilityTypes.NOT_MIGRATED_TYPE
  }

  const { availabilityStatusPresentation, availabilityDetails, internalStock } = availability
  const availabilityKey = availabilityDetails?.key
  const isDownloadable = availabilityKey === AvailabilityStatusType.ProductAvailabilityEsd

  if (internalStock?.showAlwaysInStock && !isDownloadable) {
    return AvailabilityTypes.SHOW_ALWAYS_IN_STOCK
  }

  if (availabilityKey === AvailabilityStatusType.ProductDistributorLeadTimeOneTwoDays) {
    return AvailabilityTypes.SUPPLIER_STOCK_SHIPPING_WITHIN_48H
  }

  if (availabilityKey && LONGER_SHIPPING_TYPES.includes(availabilityKey)) {
    return AvailabilityTypes.SUPPLIER_STOCK_SHIPPING_WITHIN_DAYS
  }

  if (
    availabilityStatusPresentation === AvailabilityStatusPresentation.ShowAsImmediateShipping &&
    !isDownloadable
  ) {
    return AvailabilityTypes.IN_STOCK_IMMEDIATE_SHIPPING
  }
  if (isDownloadable) {
    return AvailabilityTypes.IMMEDIATE_DOWNLOAD
  }

  if (isEstimatedInStore()) {
    return AvailabilityTypes.ESTIMATED_IN_STORE_BY_DATE
  }

  if (availabilityKey === AvailabilityStatusType.ProductEtaLeadTimeEnumOne2TwoWeeks) {
    return AvailabilityTypes.WEEKS_FROM_ORDER_DATE
  }

  if (
    availabilityKey === AvailabilityStatusType.ProductAvailabilityShippingDateAtTheEarliest ||
    availabilityKey === AvailabilityStatusType.ProductAvailabilityShippingDate
  ) {
    return AvailabilityTypes.NON_STOCK_ITEM_EARLIEST_DATE
  }
  if (availabilityKey === AvailabilityStatusType.ProductAvailabilityOutOfStockComingSoon) {
    return AvailabilityTypes.PRODUCT_AVAILABILITY_OUT_OF_STOCK_COMING_SOON
  }
  if (availabilityStatusPresentation === AvailabilityStatusPresentation.ShowAsOutOfStock) {
    return AvailabilityTypes.NON_STOCK_ITEM
  }

  return AvailabilityTypes.NOT_MIGRATED_TYPE

  // Helper function to check estimated in store condition
  function isEstimatedInStore(): boolean {
    return (
      !isDownloadable &&
      (availabilityKey === AvailabilityStatusType.ProductAvailabilityEstimatedShippingDate ||
        availabilityKey === AvailabilityStatusType.ProductEstimatedInOurStore)
    )
  }
}
export function getIconColor(
  availabilityStatusPresentation?: string,
  availabilityStatus?: string,
  availabilityDetail?: string,
  isBid?: boolean,
  isNL?: boolean
): IconColor {
  if (isNL && availabilityDetail === AvailabilityStatusType.ProductDistributorLeadTimeOneTwoDays) {
    return 'green'
  }
  if (
    availabilityStatusPresentation === AvailabilityStatusPresentation.ShowAsImmediateShipping ||
    (availabilityStatus === AvailabilityStatus.ExternalStock && isBid)
  ) {
    return 'green'
  }
  if (availabilityStatusPresentation === AvailabilityStatusPresentation.ShowAsExternalStock) {
    return 'orange'
  }
  if (availabilityStatusPresentation === AvailabilityStatusPresentation.ShowAsComingSoon) {
    return 'gray'
  }

  return 'white'
}

export function getAvailabilityInfo(
  availability: GenericAvailabilityType,
  translate: Translator['translate'],
  translationsExist: (key: string) => boolean,
  isBid: boolean,
  isNL: boolean
): AvailabilityText {
  const { availabilityDetails, showAvailabilityQuantities } = availability
  const availabilityType = getAvailabilityType(availability)

  // Get basic info
  const color = getIconColor(
    availability.availabilityStatusPresentation,
    availability.availabilityStatus,
    availabilityDetails?.key,
    isBid,
    isNL
  )

  const { isEstimatedShippingDate, tooltipText } = getBasicInfo(
    availability,
    translate,
    translationsExist
  )

  const quantities: StockQuantities = {
    internal: availability.internalStock?.quantity || 0,
    external: availability.externalStock?.quantity || 0,
    internalEta: availability.internalEtaStock?.quantity || 0,
  }

  const stockNumbersText = getStockNumbersText(
    quantities,
    showAvailabilityQuantities,
    isBid,
    translate,
    availabilityType,
    availability.availabilityDetails?.formattedDateUtc
  )

  const { stockInfoText, deliveryInfoText } = getAvailabilityTexts(
    availabilityType,
    availabilityDetails,
    stockNumbersText,
    translate,
    availability
  )

  return {
    isEstimatedShippingDate,
    tooltipText,
    stockInfoText,
    deliveryInfoText,
    color,
    bidInfo: '',
  }
}

function getBasicInfo(
  availability: GenericAvailabilityType,
  translate: Translator['translate'],
  translationsExist: (key: string) => boolean
) {
  const key = availability.availabilityDetails?.key?.replace(/_/g, '') || ''
  const id = `AvailabilityText_${key}`

  const isEstimatedShippingDate =
    translationsExist('AvailabilityText_ProductAvailabilityEstimatedShippingDate_Tooltip') &&
    availability.availabilityDetails?.key ===
      AvailabilityStatusType.ProductAvailabilityEstimatedShippingDate

  const tooltipText = translate(id, {
    date: availability.availabilityDetails?.formattedDateUtc,
  })

  return { isEstimatedShippingDate, tooltipText }
}

export function getStockNumbersText(
  quantities: StockQuantities,
  showAvailabilityQuantities: boolean | undefined,
  isBid: boolean | undefined,
  translate: Translator['translate'],
  availabilityType: AvailabilityTypeKeys,
  internalEta?: Maybe<string>
): string {
  const { internal, external, internalEta: internalEtaQuantity } = quantities
  const totalQuantity = internal + external + internalEtaQuantity
  if (showAvailabilityQuantities && !isBid) {
    if (internal > 0 || external > 0 || internalEtaQuantity) {
      return translate('Stock_AsNumbers', {
        external,
        internal,
      })
    }
  } else {
    if (
      availabilityType === AvailabilityTypes.ESTIMATED_IN_STORE_BY_DATE &&
      internalEtaQuantity &&
      internalEta
    ) {
      return translate('AvailabilityText_ProductEstimatedInOurStore_with_quantity', {
        quantity: internalEtaQuantity < 100 ? internalEtaQuantity : '+100',
        date: internalEta,
      })
    }
    if (availabilityType === AvailabilityTypes.IN_STOCK_IMMEDIATE_SHIPPING && internal) {
      return translate('Stock_NumberOfItemsLeft', { quantity: internal < 100 ? internal : '+100' })
    }
    let stockQuantity = totalQuantity
    if (stockQuantity <= 0 && isBid) stockQuantity = external

    if (stockQuantity > 0 && stockQuantity <= 100) {
      return translate('Stock_NumberOfItemsLeft', { quantity: stockQuantity })
    }
    if (stockQuantity > 100) {
      return translate('Stock_MoreThanItemsLeft', { quantity: 100 })
    }
  }

  return ''
}

function getAvailabilityTexts(
  availabilityType: string,
  availabilityDetails: GenericAvailabilityType['availabilityDetails'],
  stockNumbersText: string,
  translate: Translator['translate'],
  availability: GenericAvailabilityType
) {
  const formattedDate = availabilityDetails?.formattedDateUtc

  let stockInfoText = ''
  let deliveryInfoText = ''
  const key = availabilityDetails?.key ? availabilityDetails.key.replace(/_/g, '') : ''
  const id = `AvailabilityText_${key}`

  switch (availabilityType) {
    case AvailabilityTypes.SHOW_ALWAYS_IN_STOCK:
      stockInfoText = translate('AvailabilityText_ProductAvailability_InStock') // TODO: find translations
      deliveryInfoText = translate('AvailabilityText_ProductAvailabilityImmediateShipping')
      break
    case AvailabilityTypes.IN_STOCK_IMMEDIATE_SHIPPING:
      stockInfoText = stockNumbersText
      deliveryInfoText = translate('AvailabilityText_ProductAvailabilityImmediateShipping')
      break
    case AvailabilityTypes.NON_STOCK_ITEM:
      stockInfoText = translate('AvailabilityText_ProductAvailabilityOutOfStock_Short')
      deliveryInfoText = translate('availabilitytext_non_stock')
      break

    case AvailabilityTypes.NON_STOCK_ITEM_EARLIEST_DATE:
      stockInfoText = translate('AvailabilityText_ProductAvailabilityShippingDateAtTheEarliest', {
        date: formattedDate,
      })
      deliveryInfoText = translate('availabilitytext_non_stock')
      break

    case AvailabilityTypes.SUPPLIER_STOCK_SHIPPING_WITHIN_48H:
      stockInfoText = availability.showAvailabilityQuantities
        ? `${translate('availabilitytext_supplier_stock')} (${stockNumbersText})`
        : translate('availabilitytext_supplier_stock')
      deliveryInfoText = translate('AvailabilityText_ProductDistributorLeadTimeOneTwoDays')
      break

    case AvailabilityTypes.ESTIMATED_IN_STORE_BY_DATE:
      stockInfoText = stockNumbersText
      deliveryInfoText = translate('availabilitytext_non_stock')
      break

    case AvailabilityTypes.IMMEDIATE_DOWNLOAD:
      stockInfoText = stockNumbersText
      deliveryInfoText = translate('AvailabilityText_ProductAvailabilityEsd')
      break

    case AvailabilityTypes.WEEKS_FROM_ORDER_DATE:
      stockInfoText = translate('availabilitytext_EtaLeadTimeOne2TwoWeeks')
      deliveryInfoText = translate('availabilitytext_non_stock')
      break

    case AvailabilityTypes.SUPPLIER_STOCK_SHIPPING_WITHIN_DAYS:
      stockInfoText = availability.showAvailabilityQuantities
        ? `${translate('availabilitytext_supplier_stock')} (${stockNumbersText})`
        : translate('availabilitytext_supplier_stock')
      deliveryInfoText = getShippingDaysText(availabilityDetails?.key || '', translate)
      break
    case AvailabilityTypes.PRODUCT_AVAILABILITY_OUT_OF_STOCK_COMING_SOON:
      stockInfoText = translate(id)
      deliveryInfoText = translate('availabilitytext_non_stock')
  }

  return { stockInfoText, deliveryInfoText }
}

function getShippingDaysText(key: string, translate: Translator['translate']): string {
  return key && SHIPPING_DAYS_TRANSLATIONS[key as keyof typeof SHIPPING_DAYS_TRANSLATIONS]
    ? translate(SHIPPING_DAYS_TRANSLATIONS[key as keyof typeof SHIPPING_DAYS_TRANSLATIONS])
    : ''
}
export function getOriginAvailabilityInfo(
  availability: GenericAvailabilityType,
  translate: Translator['translate'],
  translationsExist: (key: string) => boolean,
  listForm?: boolean,
  isBid?: boolean,
  isNL?: boolean
) {
  const {
    availabilityStatusPresentation,
    availabilityDetails,
    availabilityStatus,
    showAvailabilityQuantities,
  } = availability

  const internalStock =
    availability.internalStock && availability.internalStock.quantity
      ? availability.internalStock.quantity
      : 0
  const externalStock =
    availability.externalStock && availability.externalStock.quantity
      ? availability.externalStock.quantity
      : 0
  const internalEtaStock = availability.internalEtaStock?.quantity ?? 0

  let stockNumbersText = ''
  if (showAvailabilityQuantities && !isBid) {
    if (internalStock > 0 || externalStock > 0) {
      stockNumbersText = translate('Stock_AsNumbers', {
        external: externalStock,
        internal: internalStock,
      })
    }
  } else {
    let stockQuantity = internalStock
    if (stockQuantity <= 0 && isBid) stockQuantity = externalStock

    if (stockQuantity > 0 && stockQuantity <= 100) {
      stockNumbersText = translate('Stock_NumberOfItemsLeft', { quantity: stockQuantity })
    } else if (stockQuantity > 100) {
      stockNumbersText = translate('Stock_MoreThanItemsLeft', { quantity: 100 })
    }
  }

  const key = availabilityDetails?.key ? availabilityDetails.key.replace(/_/g, '') : ''
  const id = `AvailabilityText_${key}`

  const titleText = translate(id, { date: availabilityDetails?.formattedDateUtc })
  let titleTextShort = ''

  if (!stockNumbersText.length && translationsExist(`${id}_Short`)) {
    titleTextShort = translate(`${id}_Short`, { date: availabilityDetails?.formattedDateUtc })
  } else if (key === 'PRODUCTESTIMATEDINOURSTORE') {
    titleTextShort = availabilityDetails?.formattedDateUtc
      ? availabilityDetails?.formattedDateUtc
      : titleText
  } else {
    titleTextShort = titleText
  }

  let stockText = ''
  if (isBid) {
    stockText = stockNumbersText.length
      ? `${stockNumbersText} ${translate('AvailabilityText_Bid')}`
      : titleText
  } else if (listForm) {
    stockText = stockNumbersText.length ? stockNumbersText : titleTextShort
  } else {
    stockText = stockNumbersText.length ? `${titleText} (${stockNumbersText})` : titleText

    if (key === 'PRODUCTESTIMATEDINOURSTORE' && internalEtaStock > 0) {
      stockText = `${internalEtaStock > 100 ? '+100' : internalEtaStock} ${stockText.toLowerCase()}`
    }
  }

  let tooltipText = titleText
  if (key == 'PRODUCTESTIMATEDINOURSTORE' && internalEtaStock > 0) {
    tooltipText = `${
      internalEtaStock > 100 ? '+100' : internalEtaStock
    } ${titleTextShort.toLowerCase()}`
  }
  const isEstimatedShippingDate =
    translationsExist('AvailabilityText_ProductAvailabilityEstimatedShippingDate_Tooltip') &&
    key === 'PRODUCTAVAILABILITYESTIMATEDSHIPPINGDATE'
  if (isEstimatedShippingDate) {
    tooltipText = translate('AvailabilityText_ProductAvailabilityEstimatedShippingDate_Tooltip')
  }

  const color = getIconColor(
    availabilityStatusPresentation,
    availabilityStatus,
    availabilityDetails?.key,
    isBid,
    isNL
  )
  return {
    isEstimatedShippingDate,
    tooltipText,
    color,
    stockText,
  }
}
